// Pusher client — singleton wrapper around `pusher-js` that mirrors the
// setup mdg-frontend uses (`src/utils/pusher/index.js`). Two entry points:
//
//   • `pusherInstance()`  — lazily builds the singleton on first call,
//                           signs the user in, and returns the client.
//                           Returns `undefined` when no token is present
//                           so callers can no-op before login.
//   • `disconnectPusher()` — tears down the singleton on logout so the
//                            websocket doesn't outlive the session.
//
// The auth endpoint lives in `src/api/index.js` under `APIs.AUTH.verifyPusher`.
// pusher-js forwards `userAuthentication.headers` verbatim to that endpoint
// when subscribing to a private user channel, which is how the backend
// scopes each user to their tenant (we send `branch-id`, mirroring the
// header the regular `apiClient` interceptor attaches).

import Pusher from 'pusher-js'

import APIs from '../../api'
import { PLATFORM } from '../../constants'
import { tokenStorage } from '../tokenStorage'
import { getSelectedBranchMeta } from '../userStorage'

let pusher = null

export const pusherInstance = () => {
  if (pusher) return pusher

  const token = tokenStorage.getAccessToken()
  if (!token) return undefined

  const apiKey = import.meta.env.VITE_PUSHER_API_KEY
  const cluster = import.meta.env.VITE_PUSHER_CLUSTER
  if (!apiKey || !cluster) {
    // Don't blow up local dev when the env vars haven't been populated yet
    // — the surrounding hooks already no-op cleanly on `undefined`. The
    // console message gives the operator enough to fix their `.env`.
    console.warn(
      '[pusher] VITE_PUSHER_API_KEY / VITE_PUSHER_CLUSTER are not set; skipping sign-in.'
    )
    return undefined
  }

  pusher = new Pusher(apiKey, {
    cluster,
    userAuthentication: {
      endpoint: APIs.AUTH.verifyPusher,
      headers: {
        Authorization: token,
        Platform: PLATFORM.GUARDIAN,
        ...buildTenantHeaders(),
      },
    },
    autoReconnect: true,
    maxReconnectAttempts: 10,
    timeout: 30000,
  })
  pusher.signin()

  return pusher
}

export const disconnectPusher = () => {
  if (pusher) {
    pusher.disconnect()
    pusher = null
  }
}

// Mirrors the `branch-id` header the regular `apiClient` request
// interceptor attaches. The Pusher auth endpoint uses it to scope the
// private user channel to the right branch context.
function buildTenantHeaders() {
  try {
    const meta = getSelectedBranchMeta()
    if (!meta) return {}
    // if (meta.uuid) return { 'branch-id': meta.uuid }
    if (meta.id != null) return { 'branch-id': String(meta.id) }
    return {}
  } catch {
    return {}
  }
}
